/* OXXRING v2 · O-ring groove (gland) design tool — sections/oxx-ic-groove.liquid.
   Modes: check a groove, find listed O-rings that fit a groove, design a groove from an O-ring.
   Geometry is computed in mm. Reference ranges and AS568 series geometry come from assets/oxx-ic-gland.json
   (built by _oxx_theme_tooling/v2/build-gland.mjs). Results are reference geometry checks, not an approval.

   Model (nominal dimensions, no tolerance stack):
   - Radial gland depth: piston (bore − groove Ø) / 2; rod (groove Ø − rod) / 2. Face: entered depth.
   - Installed CS ≈ CS × (1 − 0.5 × stretch). First-order thinning of the section when the ring ID is
     stretched (piston groove, rod interference, face inner wall). Circumferential compression is ignored.
   - Squeeze = (installed CS − gland depth) / installed CS.
   - Gland fill = (π/4 × installed CS²) / (gland depth × groove width); corner radii ignored.
   - Diameter fit: piston stretch (groove Ø − ID)/ID; rod interference (rod − ID)/ID;
     face internal pressure: interference of the ring's outer edge on the groove outer wall;
     face external pressure: ID stretch on the groove inner wall.
   - Off-centre squeeze assumes the parts can shift by the full radial gap. */
(function () {
  'use strict';
  var IN = 25.4;
  var DEF = {
    rules: { squeeze: { face: [20, 30], static: [18, 25], recip: [10, 20], rotary: [0, 10] }, stretch: [0, 5], fill: [0, 75], finish: { staticLiquid: 32, staticGas: 16, dynamic: [8, 16] } },
    series: [], dash: {}
  };
  var FIT = {
    piston: { ref: 'stretch', lo: -3, hi: 8 },
    rod: { ref: [0, 2], lo: -3, hi: 5 },
    'face-int': { ref: [0, 3], lo: -3, hi: 5 },
    'face-ext': { ref: 'stretch', lo: -3, hi: 8 }
  };
  var RANK = { ok: 0, check: 1, out: 2 };

  /* ================= calculation core (pure, unit-tested in node) ================= */
  function isFace(app) { return app === 'face-int' || app === 'face-ext'; }
  function r1(v) { return Math.round(v * 10) / 10; }
  function sqRef(rules, app, motion) { return isFace(app) ? rules.squeeze.face : (rules.squeeze[motion] || rules.squeeze.static); }
  function fitRef(rules, app) { var f = FIT[app]; return f.ref === 'stretch' ? rules.stretch : f.ref; }
  function grade(v, ref, lo, hi) { v = r1(v); if (v >= ref[0] && v <= ref[1]) return 'ok'; if (v >= lo && v <= hi) return 'check'; return 'out'; }
  function worst(list) { var w = 'ok'; list.forEach(function (s) { if (s && RANK[s] > RANK[w]) w = s; }); return w; }
  function csAt(cs, stretchPct) { return cs * (1 - 0.5 * Math.min(40, Math.max(0, stretchPct)) / 100); }
  function area(cs) { return Math.PI / 4 * cs * cs; }

  function seriesMatch(data, cs) {
    var s = (data.series || []).filter(function (x) { return Math.abs(x.cs * IN - cs) <= 0.01; });
    return s[0] || null;
  }
  function nearestSeries(data, cs) {
    var best = null;
    (data.series || []).forEach(function (x) { if (!best || Math.abs(x.cs * IN - cs) < Math.abs(best.cs * IN - cs)) best = x; });
    return best;
  }
  function gapRef(data, cs) {
    var s = nearestSeries(data, cs);
    return (s && s.static && s.static.gap ? s.static.gap : (cs / IN < 0.17 ? 0.002 : 0.003)) * IN;
  }
  // Corner radii (mm): series values for AS568 cross-sections, interpolated by CS otherwise.
  function radii(data, cs, kind) {
    var list = (data.series || []).filter(function (x) { return x[kind]; });
    if (!list.length) return { r1: Math.max(0.1, 0.1 * cs), r2: 0.13, exact: false };
    var m = seriesMatch(data, cs);
    if (m && m[kind]) return { r1: m[kind].r1 * IN, r2: m[kind].r2 * IN, exact: true };
    var lo = null, hi = null;
    list.forEach(function (x) { var c = x.cs * IN; if (c <= cs && (!lo || c > lo.cs * IN)) lo = x; if (c >= cs && (!hi || c < hi.cs * IN)) hi = x; });
    var a, b;
    if (lo && hi) {
      var t = (cs - lo.cs * IN) / (hi.cs * IN - lo.cs * IN || 1);
      a = lo[kind].r1 + t * (hi[kind].r1 - lo[kind].r1); b = lo[kind].r2 + t * (hi[kind].r2 - lo[kind].r2);
    } else {
      var n = lo || hi; a = n[kind].r1 * cs / (n.cs * IN); b = n[kind].r2;
    }
    var q = function (v) { return Math.max(0.05, Math.round(v * IN / 0.05) * 0.05); };
    return { r1: q(a), r2: q(b), exact: false };
  }

  // Evaluate a housing (mm) with a ring {id, cs} (mm).
  function evaluate(h, ring, data) {
    data = data || DEF;
    var rules = data.rules, app = h.app, face = isFace(app), id = ring.id, cs = ring.cs;
    var r = { app: app, motion: face ? 'static' : (h.motion || 'static'), errors: [], notes: [], st: {} };
    if (!(id > 0) || !(cs > 0)) r.errors.push('ring');
    if (face) {
      if (!(h.gid > 0) || !(h.god > 0) || !(h.depth > 0)) r.errors.push('housing');
      else if (h.god <= h.gid) r.errors.push('face-order');
      r.width = (h.god - h.gid) / 2; r.depth = h.depth;
    } else if (app === 'piston') {
      if (!(h.bore > 0) || !(h.groove > 0) || !(h.width > 0)) r.errors.push('housing');
      else if (h.bore <= h.groove) r.errors.push('piston-order');
      else if (h.clr > 0 && (h.clr >= h.bore || h.clr <= h.groove)) r.errors.push('clr-piston');
      r.depth = (h.bore - h.groove) / 2; r.width = h.width;
      if (h.clr > 0) r.gap = (h.bore - h.clr) / 2;
    } else {
      if (!(h.rod > 0) || !(h.groove > 0) || !(h.width > 0)) r.errors.push('housing');
      else if (h.groove <= h.rod) r.errors.push('rod-order');
      else if (h.clr > 0 && (h.clr <= h.rod || h.clr >= h.groove)) r.errors.push('clr-rod');
      r.depth = (h.groove - h.rod) / 2; r.width = h.width;
      if (h.clr > 0) r.gap = (h.clr - h.rod) / 2;
    }
    if (r.errors.length) return r;
    var outer = id + 2 * cs; // ring outer edge, used internally only
    var s = 0;
    if (app === 'piston') { r.fit = (h.groove - id) / id * 100; s = r.fit; }
    else if (app === 'rod') { r.fit = (h.rod - id) / id * 100; s = r.fit; }
    else if (app === 'face-ext') { r.fit = (h.gid - id) / id * 100; s = r.fit; if (outer > h.god + 1e-9) r.notes.push('outer-contact'); }
    else { r.fit = (outer - h.god) / outer * 100; if (id < h.gid - 1e-9) { s = (h.gid - id) / id * 100; r.notes.push('inner-stretch'); } }
    r.stretch = Math.max(0, s);
    // Far outside any sensible fit: most often a unit mix-up or the wrong ring. Flag it instead of computing nonsense.
    r.mismatch = Math.abs(r.fit) > 25 || r.stretch > 25;
    r.csInst = csAt(cs, r.stretch);
    r.squeeze = (r.csInst - r.depth) / r.csInst * 100;
    r.fill = area(r.csInst) / (r.depth * r.width) * 100;
    var sq = sqRef(rules, app, r.motion);
    r.ref = { squeeze: sq, fit: fitRef(rules, app), fill: rules.fill };
    r.st.squeeze = r.squeeze <= 0 ? 'out' : grade(r.squeeze, sq, sq[0] - 4, sq[1] + 4);
    r.st.fit = grade(r.fit, r.ref.fit, FIT[app].lo, FIT[app].hi);
    r.st.fill = r.width < r.csInst ? 'out' : grade(r.fill, rules.fill, 0, 90);
    if (r.width < r.csInst) r.notes.push('narrow');
    if (r.depth > 3 * cs || r.width > 8 * cs) r.notes.push('dim-mismatch');
    if (r.gap != null) {
      r.ref.gap = gapRef(data, cs);
      r.st.gap = r.gap <= r.ref.gap * 1.001 ? 'ok' : r.gap <= r.ref.gap * 3 ? 'check' : 'out';
      r.sqLo = (r.csInst - (r.depth + r.gap)) / r.csInst * 100;
      r.sqHi = (r.csInst - (r.depth - r.gap)) / r.csInst * 100;
      r.st.conc = r.sqLo <= 0 ? 'out' : (r1(r.sqLo) >= sq[0] && r1(r.sqHi) <= sq[1]) ? 'ok' : 'check';
    }
    r.core = r.mismatch ? 'out' : worst([r.st.squeeze, r.st.fit, r.st.fill]);
    r.worst = worst([r.core, r.st.gap, r.st.conc]);
    return r;
  }

  // Rounding in the working unit: 0.01 mm or 0.001 in; widths round up to 0.05 mm or 0.005 in.
  function rnd(v, unit) { var q = unit === 'in' ? IN / 1000 : 0.01; return Math.round(v / q) * q; }
  function rndUp(v, unit) { var q = unit === 'in' ? IN * 0.005 : 0.05; return Math.ceil(v / q - 1e-6) * q; }
  function fillWidth(csI, depth) { return area(csI) / (depth * 0.75) * 1.10; }

  // Starting-point gland for a ring. anchor = fixed bore (piston) or rod (rod) in mm, optional.
  function design(ring, app, motion, anchor, data, unit) {
    data = data || DEF;
    var rules = data.rules, face = isFace(app), id = ring.id, cs = ring.cs;
    motion = face ? 'static' : (motion || 'static');
    var sq = sqRef(rules, app, motion), mid = (sq[0] + sq[1]) / 2, half = (sq[1] - sq[0]) / 2;
    var ser = seriesMatch(data, cs), kind = face ? 'face' : motion === 'static' ? 'static' : 'dynamic';
    var h = { app: app, motion: motion }, o = { app: app, motion: motion, ring: ring, series: ser ? ser.cs : null, notes: [], h: h };
    var csI, depth, width;
    if (face) {
      var outer = id + 2 * cs;
      csI = csAt(cs, app === 'face-ext' ? 1.5 : 0);
      depth = ser ? ser.face.d * IN : rnd(csI * (1 - mid / 100), unit);
      width = rndUp(fillWidth(csI, depth), unit);
      if (ser) {
        o.tight = { w: ser.face.w * IN, fill: area(csI) / (depth * ser.face.w * IN) * 100 };
        if (width < o.tight.w) width = o.tight.w;
        if (ser.dovetail) o.dovetail = { w: ser.dovetail.w * IN, d: ser.dovetail.d * IN, r1: ser.dovetail.r1 * IN, r2: ser.dovetail.r2 * IN };
      }
      if (app === 'face-int') { h.god = rnd(outer * 0.99, unit); h.gid = h.god - 2 * width; }
      else { h.gid = rnd(id * 1.015, unit); h.god = h.gid + 2 * width; }
      h.depth = depth;
    } else if (app === 'piston') {
      var st = rules.stretch;
      if (anchor > 0) {
        // Depth and bore trade off against each other, so search the depth that best satisfies both the squeeze
        // and the stretch reference range for this fixed bore.
        var best = null, bestAny = null, lo = cs * 0.45, hi = Math.min(cs * 1.05, (anchor - 0.6 * id) / 2);
        for (var i = 0; i <= 2400 && hi > lo; i++) {
          var d = lo + (hi - lo) * i / 2400, B = anchor - 2 * d;
          if (!(B > 0) || !(d > 0)) continue;
          var s = (B - id) / id * 100, ci = csAt(cs, s), q = (ci - d) / ci * 100;
          var vSq = Math.max(0, sq[0] + 0.5 - q, q - (sq[1] - 0.5)) / half, vSt = Math.max(0, st[0] + 0.3 - s, s - (st[1] - 0.3)) / 2.5;
          var score = (vSq + vSt) * 100 + Math.abs(q - mid) / half;
          if (!bestAny || score < bestAny.score) bestAny = { d: d, score: score };
          if (vSq === 0 && vSt === 0 && (!best || score < best.score)) best = { d: d, score: score };
        }
        var pick = best || bestAny;
        depth = rnd(pick ? pick.d : cs * (1 - mid / 100), unit);
        h.bore = anchor; h.groove = anchor - 2 * depth;
        csI = csAt(cs, (h.groove - id) / id * 100);
        if (!best) { o.notes.push('bore-conflict'); o.suggestId = (anchor - 2 * csAt(cs, 2) * (1 - mid / 100)) / 1.02; }
      } else {
        csI = csAt(cs, 2);
        depth = rnd(csI * (1 - mid / 100), unit);
        h.bore = rnd(id * 1.02 + 2 * depth, unit); h.groove = h.bore - 2 * depth;
        csI = csAt(cs, (h.groove - id) / id * 100);
      }
    } else {
      h.rod = anchor > 0 ? anchor : rnd(id * 1.01, unit);
      var fit = (h.rod - id) / id * 100;
      csI = csAt(cs, fit);
      depth = rnd(csI * (1 - mid / 100), unit);
      h.groove = h.rod + 2 * depth;
      if (r1(fit) < 0 || r1(fit) > 2) { o.notes.push('rod-fit'); o.suggestId = h.rod / 1.01; }
    }
    if (!face) {
      width = ser ? ser[kind].w * IN : rndUp(fillWidth(csI, depth), unit);
      h.width = width;
      if (ser) o.widthTol = ser[kind].wt * IN;
    } else if (ser) o.widthTol = ser.face.wt * IN;
    if (motion === 'rotary') o.notes.push('rotary');
    o.depth = depth; o.width = width; o.csInst = csI;
    o.win = [csI * (1 - sq[1] / 100), csI * (1 - sq[0] / 100)];
    o.radii = radii(data, cs, kind);
    var f = rules.finish;
    o.finish = motion === 'static' ? { liquid: f.staticLiquid, gas: f.staticGas } : { dynamic: f.dynamic };
    o.eval = evaluate(h, ring, data);
    return o;
  }

  // Public dash-table gland for an AS568 ring (mm), or null.
  function refGland(data, dash, ring, app, motion) {
    var d = data && data.dash && data.dash[dash], ser = seriesMatch(data || DEF, ring.cs);
    if (!d || !ser || motion === 'rotary') return null;
    var h = { app: app, motion: isFace(app) ? 'static' : motion };
    if (app === 'piston' || app === 'rod') {
      var dyn = motion !== 'static', row = dyn ? d.d : d.s;
      if (!row) return null;
      h.width = ser[dyn ? 'dynamic' : 'static'].w * IN;
      if (app === 'piston') { h.bore = row[0] * IN; h.groove = row[1] * IN; if (dyn) h.clr = row[2] * IN; }
      else if (dyn) { h.rod = row[3] * IN; h.groove = row[4] * IN; h.clr = row[5] * IN; }
      else { h.rod = row[2] * IN; h.groove = row[3] * IN; }
    } else {
      if (!d.f) return null;
      var w = ser.face.w * IN; h.depth = ser.face.d * IN;
      if (app === 'face-ext') { h.gid = d.f[0] * IN; h.god = h.gid + 2 * w; }
      else { h.god = d.f[1] * IN; h.gid = h.god - 2 * w; }
    }
    return h;
  }

  // Rank listed sizes {id, cs, codes} for a housing. Keeps rows whose squeeze, fit and fill are not "out".
  function rank(h, rows, data, opt) {
    data = data || DEF; opt = opt || {};
    var rules = data.rules, sq = sqRef(rules, h.app, isFace(h.app) ? 'static' : h.motion), fr = fitRef(rules, h.app);
    var sqMid = (sq[0] + sq[1]) / 2, sqHalf = (sq[1] - sq[0]) / 2 || 1, frMid = (fr[0] + fr[1]) / 2, frHalf = (fr[1] - fr[0]) / 2 || 1;
    var out = [];
    rows.forEach(function (row) {
      if (opt.mat && !row.codes.some(function (c) { return c.m === opt.mat; })) return;
      var e = evaluate(h, { id: row.id, cs: row.cs }, data);
      if (e.errors.length || e.core === 'out') return;
      // Distance from the middle of each range; fill above 65 % is penalised so sizes near the 75 % limit rank lower.
      var score = Math.abs(e.squeeze - sqMid) / sqHalf + Math.abs(e.fit - frMid) / frHalf + Math.max(0, e.fill - 65) / 5;
      out.push({ row: row, e: e, tier: RANK[e.core], score: score });
    });
    return out.sort(function (a, b) { return a.tier - b.tier || a.score - b.score; });
  }

  var CORE = { IN: IN, DEF: DEF, evaluate: evaluate, design: design, refGland: refGland, rank: rank, radii: radii, gapRef: gapRef, seriesMatch: seriesMatch, sqRef: sqRef, fitRef: fitRef, csAt: csAt };
  if (typeof module === 'object' && module.exports) { module.exports = CORE; return; }
  if (typeof window === 'undefined') return;
  window.OXXGroove = CORE;

  /* ================= UI ================= */
  var MATS = { N: 'NBR', F: 'FKM', E: 'EPDM', S: 'Silicone', K: 'FFKM' };
  var MAT_ORDER = 'NFESK';
  var MAT_PARAM = { N: 'NBR', F: 'FKM', E: 'EPDM', S: 'SILICONE', K: 'FFKM' };
  var KEYS = ['bore', 'groove', 'rod', 'width', 'clr', 'gid', 'god', 'depth', 'id', 'cs'];
  var APP_TXT = { piston: 'Piston (bore) seal', rod: 'Rod (shaft) seal', 'face-int': 'Face seal, internal pressure', 'face-ext': 'Face seal, external pressure' };
  var APP_PARAM = { piston: 'piston', rod: 'rod', 'face-int': 'face-internal-pressure', 'face-ext': 'face-external-pressure' };
  var MOTION_TXT = { static: 'Static', recip: 'Reciprocating', rotary: 'Rotary' };
  var MOTION_PARAM = { static: 'static', recip: 'reciprocating', rotary: 'rotary' };
  var ST_TXT = { ok: 'Within typical range', check: 'Check', out: 'Outside typical range' };
  var FIT_TXT = {
    piston: { k: 'Installed stretch', s: 'Stretch', n: 'Ring ID stretched onto the groove diameter.' },
    rod: { k: 'Rod interference', s: 'Rod fit', n: 'Rod diameter against the ring ID.' },
    'face-int': { k: 'Outer wall fit', s: 'Wall fit', n: 'Ring pressed against the groove outer wall; internal pressure pushes it outward.' },
    'face-ext': { k: 'ID stretch', s: 'ID stretch', n: 'Ring ID seated on the groove inner wall; external pressure pushes it inward.' }
  };
  var ERR = {
    ring: 'Enter the O-ring inside diameter and cross-section.',
    housing: 'Enter all required groove dimensions.',
    'piston-order': 'The bore diameter must be larger than the groove diameter.',
    'rod-order': 'The groove diameter must be larger than the rod diameter.',
    'face-order': 'The groove OD must be larger than the groove ID.',
    'clr-piston': 'The piston diameter must sit between the groove diameter and the bore.',
    'clr-rod': 'The housing bore at the rod must sit between the rod and the groove diameter.'
  };

  function el(tag, cls, text) { var n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; }
  function num(v) { var s = String(v == null ? '' : v).trim().replace(',', '.'); return /^\d*\.?\d+$/.test(s) ? parseFloat(s) : NaN; }
  function pct(v) { var x = r1(v); return (Math.abs(x) < 0.05 ? 0 : x).toFixed(1); }
  function fmtL(mm, unit) { return unit === 'in' ? (mm / IN).toFixed(3) : mm.toFixed(2); }
  function fmtAlt(mm, unit) { return unit === 'in' ? mm.toFixed(2) + ' mm' : (mm / IN).toFixed(3) + ' in'; }
  function uLabel(unit) { return unit === 'in' ? 'in' : 'mm'; }
  function track(name, detail) { window.dataLayer = window.dataLayer || []; window.dataLayer.push(Object.assign({ event: name }, detail || {})); }
  function reduced() { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  function icon(path) { return '<svg class="ic-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' + path + '</svg>'; }
  var ICON = {
    arrow: '<path d="M5 12h14M13 6l6 6-6 6"/>', search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>', wrench: '<path d="M14.5 6.5a4 4 0 0 0 4.9 4.9l-9 9a2.1 2.1 0 0 1-3-3l9-9a4 4 0 0 0-1.9-1.9z"/><path d="M19.5 3.5 16 7"/>',
    gauge: '<path d="M4 17a8 8 0 1 1 16 0"/><path d="m12 13 3.5-4M4.5 17h15"/>', link: '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/>',
    flask: '<path d="M9 3h6M10 3v6l-5.5 9.2A1.8 1.8 0 0 0 6 21h12a1.8 1.8 0 0 0 1.5-2.8L14 9V3"/><path d="M7.5 15h9"/>', layers: '<path d="m12 3 9 5-9 5-9-5z"/><path d="m3 13 9 5 9-5"/>',
    ruler: '<path d="m3 16.5 13.5-13.5 4.5 4.5L7.5 21z"/><path d="m7 12.5 2 2M10 9.5l1.5 1.5M13 6.5l2 2"/>', check: '<path d="m5 12.5 4.5 4.5L19 7.5"/>', alert: '<path d="M10.3 4.3 2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z"/><path d="M12 9.5v4M12 17h.01"/>',
    print: '<path d="M7 8V3h10v5M7 17H4V9h16v8h-3M7 14h10v7H7z"/>'
  };

  var rowCache = {}, rowQueue = [], rowActive = 0;
  function pumpRows() {
    while (rowActive < 6 && rowQueue.length) {
      var job = rowQueue.shift(); rowActive++;
      (function (job) {
        fetch('/products/' + job.handle + '?section_id=oxx-ic-row', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) { var t = document.createElement('template'); t.innerHTML = html; return t.content.querySelector('tr.ic-row') || t.content.querySelector('tr'); })
          .catch(function () { return null; })
          .then(function (tr) { rowActive--; job.done(tr); pumpRows(); });
      })(job);
    }
  }
  function fetchRow(handle) {
    if (!rowCache[handle]) rowCache[handle] = new Promise(function (done) { rowQueue.push({ handle: handle, done: done }); pumpRows(); });
    return rowCache[handle].then(function (tr) { return tr ? tr.cloneNode(true) : null; });
  }

  function init(root) {
    if (root.dataset.icReady) return;
    var X = window.OXXIC;
    if (!X) return;
    root.dataset.icReady = '1';
    var $ = function (s) { return root.querySelector(s); };
    var $$ = function (s) { return [].slice.call(root.querySelectorAll(s)); };
    var form = $('[data-ic-gr-form]');
    var tabs = $$('[data-ic-gr-tab]'), panel = $('[data-ic-gr-panel]'), out = panel;
    var verdict = $('[data-ic-gr-verdict]'), svg = $('[data-ic-gr-svg]'), cap = $('[data-ic-gr-cap]');
    var keyBox = $('[data-ic-gr-key]'), moreBox = $('[data-ic-gr-more]'), extra = $('[data-ic-gr-extra]'), actions = $('[data-ic-gr-actions]');
    var dock = $('[data-ic-gr-dock]'), quick = $('[data-ic-gr-quick]'), quickMsg = $('[data-ic-gr-quick-msg]'), refBtn = $('[data-ic-gr-ref]');
    var finder = root.dataset.finderUrl, rfq = root.dataset.rfqUrl;
    var data = DEF, as568 = null, index = null, lastGeom = null, seq = 0, timer, urlTimer;
    var S = { mode: 'check', app: 'piston', motion: 'static', unit: 'mm', mm: {}, dash: '', mat: '' };

    /* ---------- state <-> fields (a key can have one input per mode/application) ---------- */
    var KEYS = ['id', 'cs'];
    var HOUSE_KEYS = ['bore', 'groove', 'rod', 'width', 'clr', 'gid', 'god', 'depth'];
    // Housing fields are per application: a piston width and a rod width are different numbers.
    var APPKEY = { bore: 1, rod: 1, width: 1, clr: 1, groove: 1, gid: 1, god: 1, depth: 1 };
    function fieldKey(i) { var k = i.getAttribute('data-ic-gr-f'); return APPKEY[k] ? k + ':' + S.app : k; }
    function setField(i, skip) {
      if (i === skip) return;
      var k = i.dataset.k || fieldKey(i), v = S.mm[k];
      i.value = v > 0 ? fmtL(v, S.unit) : '';
    }
    function setAll() { $$('[data-ic-gr-f]').forEach(function (i) { setField(i); }); }
    function readField(i) { var k = fieldKey(i), v = num(i.value); S.mm[k] = v > 0 ? v * (S.unit === 'in' ? IN : 1) : NaN; }
    function housing() {
      var pick = function (k) { return S.mm[k + ':' + S.app] || S.mm[k]; };
      return { app: S.app, motion: S.motion, bore: pick('bore'), groove: pick('groove'), rod: pick('rod'), width: pick('width'), clr: pick('clr'), gid: pick('gid'), god: pick('god'), depth: pick('depth') };
    }
    // Re-key per application so the piston fields and the rod fields never share a value.
    function rekey() { $$('[data-ic-gr-f]').forEach(function (i) { var k = i.getAttribute('data-ic-gr-f'); i.dataset.k = APPKEY[k] ? k + ':' + S.app : k; }); }
    function ring() { return { id: S.mm.id, cs: S.mm.cs, dash: S.dash }; }
    function ringLabel(rg) { return rg.id.toFixed(2) + ' × ' + rg.cs.toFixed(2) + ' mm' + (rg.dash ? ' (AS568 -' + rg.dash + ')' : ''); }

    function applyVisibility() {
      root.dataset.mode = S.mode; root.dataset.app = S.app;
      $$('[data-modes]').forEach(function (n) { n.hidden = n.dataset.modes.split(' ').indexOf(S.mode) < 0 || (n.dataset.apps && n.dataset.apps.split(' ').indexOf(S.app) < 0); });
      $$('[data-apps]:not([data-modes])').forEach(function (n) { n.hidden = n.dataset.apps.split(' ').indexOf(S.app) < 0; });
      var hLegend = $('[data-ic-gr-fs="housing"] .ic-groove-fs__h small');
      if (hLegend) hLegend.textContent = S.mode === 'design' ? (isFace(S.app) ? 'sized from the ring' : 'optional') : 'nominal dimensions';
      $$('[data-label-' + S.app + ']').forEach(function (n) { n.textContent = n.getAttribute('data-label-' + S.app); });
      $$('[data-ic-gr-u]').forEach(function (n) { n.textContent = uLabel(S.unit); });
      var face = isFace(S.app);
      $$('input[name="motion"]').forEach(function (r) { r.disabled = face && r.value !== 'static'; r.checked = r.value === (face ? 'static' : S.motion); });
      $$('[data-ic-gr-face-note]').forEach(function (n) { n.hidden = !face; });
      // Field order: the O-ring comes first when designing a groove.
      var ringFs = $('[data-ic-gr-fs="ring"]'), houseFs = $('[data-ic-gr-fs="housing"]');
      if (S.mode === 'design') form.insertBefore(ringFs, houseFs); else form.insertBefore(houseFs, ringFs);
      var n = 0; $$('.ic-groove-fs').forEach(function (fs) { if (!fs.hidden) { var s = fs.querySelector('.ic-groove-step'); if (s) s.textContent = ++n; } });
      var go = $('[data-ic-gr-go]');
      if (go) go.textContent = { check: 'Check groove', find: 'Find O-rings', design: 'Design groove' }[S.mode];
    }

    function selectMode(mode, focus) {
      S.mode = mode;
      tabs.forEach(function (t) { var on = t.dataset.icGrTab === mode; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; if (on) { panel.setAttribute('aria-labelledby', t.id); if (focus) t.focus(); } });
      applyVisibility(); run();
    }

    /* ---------- quick fill: AS568 dash or pasted size ---------- */
    function resolveQuick(text, fromUser) {
      var t = String(text || '').trim();
      quickMsg.textContent = ''; quickMsg.className = 'ic-groove-quick-msg';
      if (!t) return Promise.resolve(false);
      var dm = t.match(/^(?:as\s*-?\s*568)?\s*[-#]?\s*(\d{1,3})\s*$/i);
      if (dm) {
        var dash = ('00' + dm[1]).slice(-3);
        return loadAs568().then(function (a) {
          var row = a && a.rows.filter(function (r) { return r.d === dash; })[0];
          if (!row) { quickMsg.textContent = 'AS568 -' + dash + ' is not in the public size reference.'; quickMsg.className += ' is-warn'; return false; }
          S.dash = dash; S.mm.id = row.im; S.mm.cs = row.cm; setAll();
          quickMsg.textContent = 'AS568 -' + dash + ' · ' + Number(row.ii).toFixed(3) + ' × ' + Number(row.ci).toFixed(3) + ' in = ' + row.im.toFixed(2) + ' × ' + row.cm.toFixed(2) + ' mm (public size reference)';
          return true;
        });
      }
      var p = X.parse ? X.parse(t) : {};
      if (p.dash && !p.id) return resolveQuick(p.dash, fromUser);
      if (p.id && p.cs) {
        // A size without a unit follows the unit toggle; "mm", "in", "inch" or " override it.
        var unitIn = p.unit === 'in' || (S.unit === 'in' && !/mm|in\b|inch|"/i.test(t));
        var id = unitIn ? +p.rawId * IN : +p.rawId, cs = unitIn ? +p.rawCs * IN : +p.rawCs;
        S.dash = ''; S.mm.id = id; S.mm.cs = cs; setAll();
        quickMsg.textContent = 'Read as ' + id.toFixed(2) + ' × ' + cs.toFixed(2) + ' mm (' + (id / IN).toFixed(3) + ' × ' + (cs / IN).toFixed(3) + ' in)';
        return Promise.resolve(true);
      }
      if (fromUser) { quickMsg.textContent = 'Type a dash number such as 214, or a size such as 20 x 2.5.'; quickMsg.className += ' is-warn'; }
      return Promise.resolve(false);
    }
    function loadAs568() { return as568 ? Promise.resolve(as568) : X.loadJSON(root.dataset.as568Url).then(function (a) { as568 = a; return a; }).catch(function () { return null; }); }
    function loadIndex() { return index ? Promise.resolve(index) : X.sizeIndex(root.dataset.indexUrl).then(function (x) { index = x; return x; }); }

    /* ---------- links ---------- */
    function finderLink(rg, mat) {
      var u = finder + '&units=mm&id=' + rg.id.toFixed(2) + '&cs=' + rg.cs.toFixed(2);
      if (mat) u += '&material=' + MAT_PARAM[mat];
      if (rg.dash) u += '&standard=AS568&standard_code=-' + rg.dash;
      return u + '&source=groove-design';
    }
    function rfqLink(ctx, rg) {
      var u = rfq + '?source=groove-design&request_mode=ENGINEERING_REVIEW&units=mm&motion=' + MOTION_PARAM[isFace(S.app) ? 'static' : S.motion] + '&application=' + APP_PARAM[S.app];
      if (rg && rg.id > 0 && rg.cs > 0) u += '&id=' + rg.id.toFixed(2) + '&cs=' + rg.cs.toFixed(2);
      if (rg && rg.dash) u += '&standard=AS568&standard_code=-' + rg.dash;
      if (S.mat) u += '&material=' + MAT_PARAM[S.mat];
      return u + '&context=' + encodeURIComponent(ctx.slice(0, 1500));
    }
    function housingText(h) {
      var u = S.unit, L = function (v) { return v > 0 ? fmtL(v, u) : '—'; };
      if (isFace(h.app)) return 'groove ID ' + L(h.gid) + ', groove OD ' + L(h.god) + ', depth ' + L(h.depth) + ' ' + uLabel(u);
      if (h.app === 'piston') return 'bore ' + L(h.bore) + ', groove Ø ' + L(h.groove) + ', width ' + L(h.width) + (h.clr > 0 ? ', piston Ø ' + L(h.clr) : '') + ' ' + uLabel(u);
      return 'rod ' + L(h.rod) + ', groove Ø ' + L(h.groove) + ', width ' + L(h.width) + (h.clr > 0 ? ', housing bore at rod ' + L(h.clr) : '') + ' ' + uLabel(u);
    }
    function evalText(e) {
      var fr = e.ref.fit;
      return 'squeeze ' + pct(e.squeeze) + '% (ref ' + e.ref.squeeze[0] + '–' + e.ref.squeeze[1] + '%, ' + ST_TXT[e.st.squeeze] + '); ' +
        FIT_TXT[e.app].s.toLowerCase() + ' ' + pct(e.fit) + '% (ref ' + fr[0] + '–' + fr[1] + '%, ' + ST_TXT[e.st.fit] + '); ' +
        'fill ' + pct(e.fill) + '% (ref ≤' + e.ref.fill[1] + '%, ' + ST_TXT[e.st.fill] + ')' +
        (e.gap != null ? '; radial gap ' + fmtL(e.gap, S.unit) + ' ' + uLabel(S.unit) + ' (' + ST_TXT[e.st.gap] + ')' : '');
    }
    function baseCtx(title) { return 'Groove design tool · ' + title + ' · ' + APP_TXT[S.app] + ' · ' + MOTION_TXT[isFace(S.app) ? 'static' : S.motion] + ' · units ' + uLabel(S.unit); }

    /* ---------- rendering helpers ---------- */
    function chip(st) { return el('span', 'ic-groove-chip ic-groove-chip--' + st, ST_TXT[st]); }
    function meter(v, ref, lo, hi) {
      var m = el('div', 'ic-groove-meter'); m.setAttribute('aria-hidden', 'true');
      var span = hi - lo, P = function (x) { return Math.max(0, Math.min(100, (x - lo) / span * 100)); };
      var band = el('span', 'ic-groove-meter__band'); band.style.left = P(ref[0]) + '%'; band.style.width = (P(ref[1]) - P(ref[0])) + '%';
      var mark = el('span', 'ic-groove-meter__mark'); mark.style.left = P(v) + '%';
      m.appendChild(band); m.appendChild(mark);
      return m;
    }
    // Status plate: k label, v value, unit, st status, ref text, note, meter spec
    function plate(o) {
      var p = el('div', 'ic-groove-plate' + (o.st ? ' ic-groove-plate--' + o.st : '') + (o.big ? ' ic-groove-plate--big' : '') + (/ \/ /.test(o.v) ? ' ic-groove-plate--pair' : ''));
      var top = el('div', 'ic-groove-plate__top'); top.appendChild(el('span', 'ic-groove-plate__k', o.k)); if (o.st) top.appendChild(chip(o.st)); p.appendChild(top);
      var v = el('div', 'ic-groove-plate__v'); v.appendChild(el('span', 'ic-num', o.v)); if (o.u) v.appendChild(el('small', null, o.u)); p.appendChild(v);
      if (o.alt) p.appendChild(el('div', 'ic-groove-plate__alt ic-num', o.alt));
      if (o.meter) p.appendChild(meter.apply(null, o.meter));
      if (o.ref) p.appendChild(el('div', 'ic-groove-plate__ref', o.ref));
      if (o.note) p.appendChild(el('p', 'ic-groove-plate__n', o.note));
      return p;
    }
    function setVerdict(st, title, text) {
      verdict.className = 'ic-groove-verdict ic-groove-verdict--' + st;
      verdict.innerHTML = icon(st === 'ok' ? ICON.check : st === 'none' ? ICON.gauge : ICON.alert);
      var b = el('div'); b.appendChild(el('strong', null, title)); if (text) b.appendChild(el('span', null, text));
      verdict.appendChild(b);
    }
    function btn(cls, text, ic, href) {
      var a = el(href ? 'a' : 'button', 'ic-btn ' + cls); if (href) a.href = href; else a.type = 'button';
      a.innerHTML = ic ? icon(ic) : ''; a.appendChild(document.createTextNode(text)); return a;
    }
    // Copy with a fallback: the async Clipboard API is refused in embedded previews and non-secure contexts, and the
    // old handler reported "Link copied" even when the copy had failed.
    function legacyCopy(t) {
      return new Promise(function (res, rej) {
        var ta = document.createElement('textarea'); ta.value = t; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(ta); ta.select();
        var ok = false; try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
        document.body.removeChild(ta); if (ok) res(); else rej(new Error('copy failed'));
      });
    }
    function copyText(t) {
      if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(t).catch(function () { return legacyCopy(t); });
      return legacyCopy(t);
    }
    function setActions(list) {
      actions.replaceChildren();
      var row = el('div', 'ic-groove-actions__main'); list.forEach(function (b) { row.appendChild(b); }); actions.appendChild(row);
      // One bar under the main buttons: related tools on the left (they open other pages), the two actions on this
      // result on the right. Print used to sit alone on its own row under everything (owner screenshot 2026-10-03).
      var bar = el('div', 'ic-groove-actions__bar');
      var links = el('nav', 'ic-groove-actions__links'); links.setAttribute('aria-label', 'Related tools');
      var c = el('a', 'ic-link'); c.href = '/pages/chemical-compatibility'; c.innerHTML = icon(ICON.flask); c.appendChild(document.createTextNode('Chemical compatibility')); links.appendChild(c);
      var m = el('a', 'ic-link'); m.href = '/pages/material-guide'; m.innerHTML = icon(ICON.layers); m.appendChild(document.createTextNode('Material guide')); links.appendChild(m);
      // Hand the same ring and application to Engineer Pro for the extrusion / pressure / dynamic review.
      var ep = el('a', 'ic-link'), q = new URLSearchParams({ unit: 'mm', source: 'groove-design' });
      if (S.mm.id) q.set('id', String(S.mm.id)); if (S.mm.cs) q.set('cs', String(S.mm.cs)); if (S.dash) q.set('standard_code', '-' + S.dash);
      if (S.mat) q.set('material', S.mat); q.set('application', S.app); q.set('motion', S.motion);
      ep.href = '/pages/bulk-quote?view=engineer-pro&' + q.toString(); ep.innerHTML = icon(ICON.gauge); ep.appendChild(document.createTextNode('Pressure & extrusion in Engineer Pro')); links.appendChild(ep);
      var util = el('div', 'ic-groove-actions__util');
      var status = el('span', 'ic-sr'); status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
      var cp = el('button', 'ic-groove-util'); cp.type = 'button'; cp.title = 'Copy a link that reopens this exact result'; cp.innerHTML = icon(ICON.link);
      var cpT = el('span', null, 'Copy link'); cp.appendChild(cpT);
      cp.addEventListener('click', function () {
        saveUrl(true);
        var reset = function () { cp.classList.remove('is-done', 'is-fail'); cpT.textContent = 'Copy link'; };
        copyText(location.href).then(function () {
          cp.classList.add('is-done'); cpT.textContent = 'Link copied'; status.textContent = 'Link to this result copied.'; setTimeout(reset, 2200);
        }, function () {
          cp.classList.add('is-fail'); cpT.textContent = 'Copy failed'; status.textContent = 'Copy failed. Copy the address from the browser bar instead.'; setTimeout(reset, 3500);
        });
      });
      var pr = el('button', 'ic-groove-util'); pr.type = 'button'; pr.title = 'Print this result or save it as a PDF'; pr.innerHTML = icon(ICON.print);
      pr.appendChild(el('span', null, 'Print / PDF'));
      pr.addEventListener('click', function () { saveUrl(true); window.print(); });
      util.appendChild(cp); util.appendChild(pr); util.appendChild(status);
      // Copy / print act on this result, so they sit at the right end of the main button row (one line on desktop);
      // the related-tool links get their own row underneath.
      row.appendChild(util);
      bar.appendChild(links);
      actions.appendChild(bar);
    }
    function empty(msg) {
      setVerdict('none', msg, '');
      keyBox.replaceChildren(); moreBox.replaceChildren(); extra.replaceChildren();
      drawGland(null); setActions([]); actions.firstChild.hidden = true; updateDock(null);
    }
    function fitPlate(e) {
      var fr = e.ref.fit, T = FIT_TXT[e.app];
      return plate({ k: T.k, v: pct(e.fit), u: '%', st: e.st.fit, meter: [e.fit, fr, -4, 10], ref: 'Reference ' + fr[0] + '–' + fr[1] + ' %', note: fitNote(e) });
    }
    function fitNote(e) {
      if (e.app === 'piston') return e.fit < 0 ? 'Ring ID is larger than the groove diameter: it sits loose and can twist on assembly.' : e.st.fit === 'ok' ? 'Ring ID stretched onto the groove diameter.' : 'High stretch thins the section and speeds ageing.';
      if (e.app === 'rod') return e.fit < 0 ? 'Ring ID is larger than the rod: the groove alone holds it; check for twisting.' : e.st.fit === 'ok' ? 'Rod diameter against the ring ID within this tool reference window; twisting also depends on installation, alignment, lubrication and motion.' : 'Rod interference above the reference stretches the ring.';
      if (e.app === 'face-int') return e.fit < 0 ? 'Groove outer wall is larger than the ring: pressure will move and stretch the ring.' : e.st.fit === 'ok' ? 'Ring pressed lightly against the groove outer wall.' : 'Heavy interference on the outer wall can buckle the ring.';
      return e.fit < 0 ? 'Groove inner wall is smaller than the ring ID: pressure will push the ring inward.' : e.st.fit === 'ok' ? 'Ring ID seated on the groove inner wall.' : 'High stretch thins the section and speeds ageing.';
    }
    function corePlates(e, big) {
      var sq = e.ref.squeeze;
      var list = [
        plate({ k: 'Squeeze', v: pct(e.squeeze), u: '%', st: e.st.squeeze, big: big, meter: [e.squeeze, sq, 0, 40], ref: 'Reference ' + sq[0] + '–' + sq[1] + ' % · ' + (isFace(e.app) ? 'face seal' : MOTION_TXT[e.motion].toLowerCase()), note: e.squeeze <= 0 ? 'No positive squeeze in the stretch-thinning approximation; review groove tolerances, sealing margin and service conditions.' : 'Stretch-thinning approximation: installed CS ' + fmtL(e.csInst, S.unit) + ' ' + uLabel(S.unit) + ' against a ' + fmtL(e.depth, S.unit) + ' ' + uLabel(S.unit) + ' gland depth. Engineer Pro uses free nominal CS without this thinning approximation.' }),
        fitPlate(e),
        plate({ k: 'Gland fill', v: pct(e.fill), u: '%', st: e.st.fill, big: big, meter: [e.fill, e.ref.fill, 0, 100], ref: 'Reference ≤ ' + e.ref.fill[1] + ' % (about 25 % free space)', note: e.notes.indexOf('narrow') >= 0 ? 'The groove is narrower than the installed cross-section.' : e.fill > e.ref.fill[1] ? 'Little room left for swell and heat expansion.' : 'Room left for swell, heat expansion and tolerances.' })
      ];
      if (big) list[1].classList.add('ic-groove-plate--big');
      return list;
    }

    /* ---------- SVG gland cross-section ---------- */
    function drawGland(g) {
      lastGeom = g;
      var W = Math.round(Math.max(300, Math.min(660, svg.getBoundingClientRect().width || 560))), H = W < 480 ? 270 : 300;
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
      if (!g) {
        svg.innerHTML = '<rect class="ic-groove-s-bg" x="0" y="0" width="' + W + '" height="' + H + '"/><text class="ic-groove-s-hint" x="' + W / 2 + '" y="' + H / 2 + '" text-anchor="middle">Enter dimensions to draw the gland</text>';
        cap.textContent = ''; return;
      }
      var face = isFace(g.app), pad = W < 480 ? 16 : 24;
      var maxW = W * (W < 480 ? 0.46 : 0.4), maxD = H * 0.4;
      var k = Math.min(maxW / g.width, maxD / Math.max(g.depth, g.cs));
      var gw = g.width * k, gd = g.depth * k, c0 = g.cs * k, ci = g.csInst * k;
      var cx = W * (W < 480 ? 0.56 : 0.5), x0 = cx - gw / 2, x1 = cx + gw / 2;
      var yM = H * 0.3, gapPx = g.gap != null ? Math.max(3, Math.min(12, g.gap * k)) : face ? 0 : 3, yL = yM + gapPx, yF = yM + gd;
      var bot = H - 18;
      var parts = face ? ['COVER', 'FLANGE'] : g.app === 'piston' ? ['BORE', 'PISTON'] : ['ROD', 'HOUSING'];
      var s = [];
      s.push('<defs><pattern id="icGrHatch" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><rect width="8" height="8" class="ic-groove-s-steel"/><line x1="0" y1="0" x2="0" y2="8" class="ic-groove-s-hatch"/></pattern>');
      s.push('<marker id="icGrArr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse"><path d="M0 1 10 5 0 9z" class="ic-groove-s-arrow"/></marker></defs>');
      s.push('<rect class="ic-groove-s-bg" x="0" y="0" width="' + W + '" height="' + H + '"/>');
      // mating part and grooved part
      s.push('<rect class="ic-groove-s-part" x="' + pad + '" y="16" width="' + (W - 2 * pad) + '" height="' + (yM - 16) + '" fill="url(#icGrHatch)"/>');
      s.push('<path class="ic-groove-s-part" fill="url(#icGrHatch)" d="M' + pad + ' ' + yL + 'H' + x0 + 'V' + yF + 'H' + x1 + 'V' + yL + 'H' + (W - pad) + 'V' + bot + 'H' + pad + 'Z"/>');
      s.push('<text class="ic-groove-s-part-t" x="' + (pad + 10) + '" y="' + (yM - 10) + '">' + parts[0] + '</text>');
      s.push('<text class="ic-groove-s-part-t" x="' + (pad + 10) + '" y="' + (bot - 10) + '">' + parts[1] + '</text>');
      // ring: nominal outline (dashed) and installed shape
      var rx, ry, squeezed = g.csInst > g.depth;
      if (squeezed) { ry = gd / 2; rx = Math.min(gw / 2 - 0.5, ci * ci / (4 * ry)); } else { rx = ry = ci / 2; }
      var rcx = g.app === 'face-int' ? x1 - rx : g.app === 'face-ext' ? x0 + rx : cx, rcy = yF - ry;
      s.push('<ellipse class="ic-groove-s-ring' + (g.fill > 90 || !squeezed ? ' ic-groove-s-ring--warn' : '') + '" cx="' + rcx + '" cy="' + rcy + '" rx="' + rx + '" ry="' + ry + '"/>');
      s.push('<ellipse class="ic-groove-s-ring-hi" cx="' + (rcx - rx * 0.28) + '" cy="' + (rcy - ry * 0.32) + '" rx="' + rx * 0.32 + '" ry="' + ry * 0.2 + '"/>');
      s.push('<circle class="ic-groove-s-nom" cx="' + rcx + '" cy="' + (yF - c0 / 2) + '" r="' + c0 / 2 + '"/>');
      // CS diameter line on the nominal circle + leader
      var a = Math.PI / 4, ncx = rcx, ncy = yF - c0 / 2, rr = c0 / 2;
      s.push('<line class="ic-groove-s-dim" x1="' + (ncx - rr * Math.cos(a)) + '" y1="' + (ncy + rr * Math.sin(a)) + '" x2="' + (ncx + rr * Math.cos(a)) + '" y2="' + (ncy - rr * Math.sin(a)) + '" marker-start="url(#icGrArr)" marker-end="url(#icGrArr)"/>');
      var lx = Math.min(W - pad - 4, x1 + (W < 480 ? 14 : 36)), ly = yM - 26;
      s.push('<polyline class="ic-groove-s-lead" points="' + (ncx + rr * Math.cos(a)) + ',' + (ncy - rr * Math.sin(a)) + ' ' + (lx - 6) + ',' + ly + ' ' + lx + ',' + ly + '"/>');
      s.push(label(lx + 4, ly + 4, 'CS ' + g.fmt(g.cs), 'start', W));
      // depth dimension (mating surface to groove floor)
      var dx = x0 - (W < 480 ? 16 : 30);
      s.push('<line class="ic-groove-s-ext" x1="' + (dx - 6) + '" y1="' + yF + '" x2="' + x0 + '" y2="' + yF + '"/>');
      s.push('<line class="ic-groove-s-dim" x1="' + dx + '" y1="' + (yM + 1) + '" x2="' + dx + '" y2="' + (yF - 1) + '" marker-start="url(#icGrArr)" marker-end="url(#icGrArr)"/>');
      s.push(label(dx - 6, (yM + yF) / 2 + 4, 'DEPTH ' + g.fmt(g.depth), 'end', W));
      // width dimension below the groove
      var wy = yF + (W < 480 ? 22 : 28);
      s.push('<line class="ic-groove-s-ext" x1="' + x0 + '" y1="' + yF + '" x2="' + x0 + '" y2="' + (wy + 6) + '"/><line class="ic-groove-s-ext" x1="' + x1 + '" y1="' + yF + '" x2="' + x1 + '" y2="' + (wy + 6) + '"/>');
      s.push('<line class="ic-groove-s-dim" x1="' + (x0 + 1) + '" y1="' + wy + '" x2="' + (x1 - 1) + '" y2="' + wy + '" marker-start="url(#icGrArr)" marker-end="url(#icGrArr)"/>');
      s.push(label(cx, wy + 20, 'WIDTH ' + g.fmt(g.width), 'middle', W));
      if (g.gap != null) s.push(label(Math.min(W - pad - 4, x1 + 16), yL + 16, 'GAP ' + g.fmt(g.gap), 'start', W));
      if (face) {
        var py = yF - gd * 0.5, px = g.app === 'face-int' ? x0 + 4 : x1 - 4, dir = g.app === 'face-int' ? 1 : -1, room = Math.abs((g.app === 'face-int' ? rcx - rx : rcx + rx) - px);
        if (room > 14) {
          var len = Math.min(room - 4, 26);
          s.push('<line class="ic-groove-s-press" x1="' + px + '" y1="' + py + '" x2="' + (px + dir * len) + '" y2="' + py + '" marker-end="url(#icGrArr)"/>');
          s.push(label(px + dir * (len + 6), py - 6, 'PRESSURE', dir > 0 ? 'start' : 'end', W, 'ic-groove-s-press-t'));
        }
      }
      // machined floor line under the groove, so the two bodies read as one section
      s.push('<line class="ic-groove-s-floor" x1="' + pad + '" y1="' + bot + '" x2="' + (W - pad) + '" y2="' + bot + '"/>');
      svg.innerHTML = s.join('');
      cap.textContent = 'Section through the gland, drawn to scale. Dashed circle: free cross-section; filled shape: installed ring' + (g.squeeze != null ? ' at ' + pct(g.squeeze) + ' % squeeze.' : '.');
    }
    function label(x, y, t, anchor, W, cls) {
      var w = t.length * 7.4 + 10, bx = anchor === 'end' ? x - w + 5 : anchor === 'middle' ? x - w / 2 : x - 5;
      bx = Math.max(2, Math.min(W - w - 2, bx));
      var tx = anchor === 'end' ? bx + w - 5 : anchor === 'middle' ? bx + w / 2 : bx + 5;
      return '<rect class="ic-groove-s-lbl-bg" x="' + bx + '" y="' + (y - 13) + '" width="' + w + '" height="18" rx="3"/><text class="' + (cls || 'ic-groove-s-lbl') + '" x="' + tx + '" y="' + y + '" text-anchor="' + anchor + '">' + t + '</text>';
    }
    function geom(h, e, rg) {
      return { app: h.app, depth: e.depth, width: e.width, cs: rg.cs, csInst: e.csInst, gap: e.gap, squeeze: e.squeeze, fill: e.fill, fmt: function (v) { return fmtL(v, S.unit); } };
    }

    /* ---------- mobile dock ---------- */
    function updateDock(e) {
      if (!dock) return;
      if (!e) { dock.hidden = true; dock.dataset.has = ''; return; }
      dock.dataset.has = '1';
      var items = dock.querySelector('[data-ic-gr-dock-items]'); items.replaceChildren();
      [['Squeeze', e.squeeze, e.st.squeeze], [FIT_TXT[e.app].s, e.fit, e.st.fit], ['Fill', e.fill, e.st.fill]].forEach(function (m) {
        var li = el('li', 'ic-groove-dock__i ic-groove-dock__i--' + m[2]); li.appendChild(el('span', null, m[0])); li.appendChild(el('strong', 'ic-num', pct(m[1]) + '%')); items.appendChild(li);
      });
      syncDock();
    }
    // The dock stays out of the way while the live result panel itself is on screen.
    function outInView() {
      var r = out.getBoundingClientRect(), vh = window.innerHeight || 800;
      return !!out.offsetParent && r.top < vh * 0.72 && r.bottom > 0;
    }
    function syncDock() { if (dock) dock.hidden = !dock.dataset.has || outInView(); }
    if (dock) {
      dock.querySelector('button').addEventListener('click', function () { out.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' }); });
      window.addEventListener('scroll', syncDock, { passive: true });
      window.addEventListener('resize', syncDock);
    }

    /* ---------- listed grades (server-rendered rows) ---------- */
    function sortCodes(codes) {
      return codes.slice().sort(function (a, b) { return MAT_ORDER.indexOf(a.m) - MAT_ORDER.indexOf(b.m) || a.h - b.h; });
    }
    function gradeChips(codes) {
      var box = el('div', 'ic-groove-mats'), by = {};
      codes.forEach(function (c) { (by[c.m] = by[c.m] || []).push(c.h); });
      MAT_ORDER.split('').forEach(function (m) { if (by[m]) { var t = el('span', 'ic-groove-mat'); t.appendChild(el('strong', null, MATS[m])); t.appendChild(document.createTextNode(' ' + by[m].sort().join('·'))); box.appendChild(t); } });
      return box;
    }
    function showGrades(host, row, rg, scroll) {
      var codes = sortCodes(row.codes.filter(function (c) { return !S.mat || c.m === S.mat; }));
      var box = el('div', 'ic-groove-grades');
      var head = el('div', 'ic-groove-grades__head');
      var h = el('h3', 'ic-h4'); h.appendChild(document.createTextNode('Listed grades · ')); h.appendChild(el('span', 'ic-num', row.id.toFixed(2) + ' × ' + row.cs.toFixed(2) + ' mm')); head.appendChild(h);
      head.appendChild(el('span', 'ic-note', codes.length + ' material / hardness option' + (codes.length === 1 ? '' : 's') + (S.mat ? ' in ' + MATS[S.mat] : '') + '. Price and ordering come from the product page.'));
      box.appendChild(head);
      var wrap = el('div', 'ic-table-wrap'), table = el('table', 'ic-table ic-table--catalog');
      table.innerHTML = '<thead><tr><th scope="col">Size (ID × CS)</th><th scope="col">Material</th><th scope="col">Size reference</th><th scope="col">Smallest pack</th><th scope="col">Ordering</th><th scope="col"><span class="ic-sr">Action</span></th></tr></thead>';
      var tbody = el('tbody'); table.appendChild(tbody); wrap.appendChild(table); box.appendChild(wrap);
      var run = seq;
      codes.slice(0, 8).forEach(function (c) {
        var handle = X.handleFor(index, row, c);
        var ph = el('tr', 'ic-row ic-row--loading'), td = el('td', null, MATS[c.m] + ' ' + c.h + 'A · loading…'); td.colSpan = 6; ph.appendChild(td); tbody.appendChild(ph);
        fetchRow(handle).then(function (tr) {
          if (run !== seq && !box.isConnected) return;
          if (tr) ph.replaceWith(tr);
          else { var a = el('a', 'ic-link', MATS[c.m] + ' ' + c.h + 'A · open product'); a.href = '/products/' + handle; td.replaceChildren(a); ph.classList.remove('ic-row--loading'); }
        });
      });
      var foot = el('p', 'ic-groove-grades__foot');
      var fl = el('a', 'ic-link', codes.length > 8 ? 'Compare all ' + codes.length + ' options in the size finder' : 'Open this size in the size finder'); fl.href = finderLink({ id: row.id, cs: row.cs, dash: rg && rg.dash }, S.mat); foot.appendChild(fl);
      box.appendChild(foot);
      host.replaceChildren(box);
      if (scroll) box.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'nearest' });
      track('oxx_groove_grades', { size: row.ids + 'x' + row.css, grades: codes.length });
    }

    /* ---------- mode: check ---------- */
    function renderCheck() {
      var h = housing(), rg = ring();
      var e = evaluate(h, rg, data);
      if (e.errors.length) {
        var missing = e.errors.every(function (x) { return x === 'ring' || x === 'housing'; });
        if (missing) return empty('Enter the groove and O-ring dimensions to see squeeze, stretch and fill.');
        setVerdict('out', ERR[e.errors[0]], ''); keyBox.replaceChildren(); moreBox.replaceChildren(); extra.replaceChildren(); drawGland(null); setActions([]); updateDock(null); return;
      }
      if (e.mismatch) {
        setVerdict('out', 'This O-ring does not match the groove size (' + FIT_TXT[e.app].s.toLowerCase() + ' ' + pct(e.fit) + ' %).', 'Check that all dimensions use the same unit (' + uLabel(S.unit) + ') and that the ring ID suits the groove diameter — or use “Find O-rings” to search sizes that fit.');
        keyBox.replaceChildren(); moreBox.replaceChildren(); extra.replaceChildren(); drawGland(null); updateDock(null);
        var s2 = btn('ic-btn--primary', 'See matching O-rings', ICON.search); s2.addEventListener('click', function () { selectMode('find'); scrollOut(); });
        setActions([s2]);
        return;
      }
      var bad = [['squeeze', 'squeeze'], ['fit', FIT_TXT[e.app].s.toLowerCase()], ['fill', 'fill'], ['gap', 'gap'], ['conc', 'off-centre squeeze']].filter(function (x) { return e.st[x[0]] && e.st[x[0]] !== 'ok'; });
      if (e.notes.indexOf('dim-mismatch') >= 0) {
        setVerdict('out', 'Check the groove dimensions and the unit: the groove is far larger than this O-ring cross-section.', 'Gland depth entered ' + fmtL(e.depth, S.unit) + ' ' + uLabel(S.unit) + ' against a ' + fmtL(rg.cs, S.unit) + ' ' + uLabel(S.unit) + ' cross-section. Plates below show what those numbers give.');
      } else if (e.worst === 'ok') setVerdict('ok', 'Squeeze, ' + FIT_TXT[e.app].s.toLowerCase() + ' and fill sit inside the typical reference ranges.', 'O-ring ' + ringLabel(rg) + ' in a ' + APP_TXT[S.app].toLowerCase() + ' groove.');
      else setVerdict(e.worst, (e.worst === 'out' ? 'Outside typical range: ' : 'To check: ') + bad.map(function (x) { return x[1]; }).join(', ') + '.', 'O-ring ' + ringLabel(rg) + ' in a ' + APP_TXT[S.app].toLowerCase() + ' groove.');
      keyBox.replaceChildren.apply(keyBox, corePlates(e, true));
      var more = [
        plate({ k: 'Gland depth', v: fmtL(e.depth, S.unit), u: uLabel(S.unit), alt: fmtAlt(e.depth, S.unit), note: isFace(S.app) ? 'Entered groove depth.' : S.app === 'piston' ? '(Bore − groove Ø) ÷ 2' : '(Groove Ø − rod) ÷ 2' }),
        plate({ k: 'Groove width', v: fmtL(e.width, S.unit), u: uLabel(S.unit), alt: fmtAlt(e.width, S.unit), note: isFace(S.app) ? '(Groove OD − groove ID) ÷ 2' : 'Axial width of the groove.' }),
        plate({ k: 'Installed CS', v: fmtL(e.csInst, S.unit), u: uLabel(S.unit), alt: fmtAlt(e.csInst, S.unit), note: e.stretch > 0 ? 'CS × (1 − ½ × ' + pct(e.stretch) + ' % stretch).' : 'No stretch: nominal cross-section.' })
      ];
      if (e.gap != null) {
        more.push(plate({ k: 'Extrusion gap', v: fmtL(e.gap, S.unit), u: uLabel(S.unit), st: e.st.gap, alt: fmtAlt(e.gap, S.unit), ref: 'Reference ≤ ' + fmtL(e.ref.gap, S.unit) + ' ' + uLabel(S.unit) + ' radial · nearest-series geometry reference', note: 'Series reference: 0.002 in for 0.070/0.103/0.139 in CS; 0.003 in for 0.210/0.275 in CS. Review the actual maximum gap, concentricity, pressure, temperature, hardness and motion; this number alone does not approve pressure service or backup-ring selection.' }));
        more.push(plate({ k: 'Off-centre squeeze', v: pct(Math.max(0, e.sqLo)) + '–' + pct(e.sqHi), u: '%', st: e.st.conc, ref: 'If the parts shift by the full gap', note: e.st.conc === 'out' ? 'On one side the ring loses contact.' : 'Squeeze range with the parts fully off-centre.' }));
      } else if (!isFace(S.app)) {
        var hint = el('div', 'ic-groove-plate ic-groove-plate--hint');
        hint.appendChild(el('span', 'ic-groove-plate__k', 'Gap & concentricity'));
        hint.appendChild(el('p', 'ic-groove-plate__n', 'Add the ' + (S.app === 'piston' ? 'piston diameter' : 'housing bore at the rod') + ' to check the extrusion gap and off-centre squeeze.'));
        more.push(hint);
      }
      moreBox.replaceChildren.apply(moreBox, more);
      drawGland(geom(h, e, rg));
      updateDock(e);
      // listing status of the entered ring
      var run = seq;
      extra.replaceChildren(el('p', 'ic-note', 'Checking the catalog for ' + rg.id.toFixed(2) + ' × ' + rg.cs.toFixed(2) + ' mm…'));
      loadIndex().then(function (ix) {
        if (run !== seq) return;
        var hit = X.exact(ix, rg.id, rg.cs)[0];
        var box = el('div', 'ic-groove-listed' + (hit ? ' is-hit' : ''));
        var txt = el('div');
        if (hit) {
          txt.appendChild(el('strong', null, 'Listed: ' + hit.id.toFixed(2) + ' × ' + hit.cs.toFixed(2) + ' mm in ' + hit.codes.length + ' grade' + (hit.codes.length === 1 ? '' : 's')));
          txt.appendChild(gradeChips(hit.codes));
          box.appendChild(txt);
          var b = btn('ic-btn--dark ic-btn--sm', 'Show grades & prices', ICON.arrow); box.appendChild(b);
          var host = el('div');
          b.addEventListener('click', function () { showGrades(host, hit, rg, true); b.hidden = true; });
          extra.replaceChildren(box, host);
        } else {
          txt.appendChild(el('strong', null, 'This exact size is not listed.'));
          txt.appendChild(el('span', 'ic-note', 'See listed sizes that fit this groove, or request it as a custom size.'));
          box.appendChild(txt);
          var f = btn('ic-btn--ghost ic-btn--sm', 'See matching O-rings', ICON.search); f.addEventListener('click', function () { selectMode('find'); scrollOut(); }); box.appendChild(f);
          extra.replaceChildren(box);
        }
      }).catch(function () { if (run === seq) extra.replaceChildren(el('p', 'ic-note', 'The size index could not load. Open the size finder to check this size.')); });
      var ctx = baseCtx('Check my groove') + ' | Housing: ' + housingText(h) + ' | O-ring ' + ringLabel(rg) + ' | ' + evalText(e) + ' | ' + location.href;
      var see = btn('ic-btn--primary', 'See matching O-rings', ICON.search); see.addEventListener('click', function () { selectMode('find'); scrollOut(); });
      setActions([see, btn('ic-btn--dark', 'Request engineering review', ICON.wrench, rfqLink(ctx, rg))]);
      track('oxx_groove_result', { mode: 'check', application: S.app, motion: S.motion, unit_system: S.unit, result: e.worst });
    }

    /* ---------- mode: find ---------- */
    function renderFind() {
      var h = housing();
      var probe = evaluate(h, { id: 10, cs: 2 }, data);
      if (probe.errors.length) {
        if (probe.errors.every(function (x) { return x === 'housing' || x === 'ring'; })) return empty('Enter the groove dimensions to search the catalog for O-rings that fit.');
        setVerdict('out', ERR[probe.errors[0]], ''); keyBox.replaceChildren(); moreBox.replaceChildren(); extra.replaceChildren(); drawGland(null); setActions([]); updateDock(null); return;
      }
      var run = seq;
      setVerdict('none', 'Searching the listed sizes…', '');
      keyBox.replaceChildren(); moreBox.replaceChildren(); extra.replaceChildren(); updateDock(null);
      loadIndex().then(function (ix) {
        if (run !== seq) return;
        var list = rank(h, ix.rows, data, { mat: S.mat });
        var okN = list.filter(function (x) { return x.tier === 0; }).length;
        var top = list.slice(0, 12);
        var ctx = baseCtx('Find O-rings for my groove') + ' | Housing: ' + housingText(h) + (S.mat ? ' | material ' + MATS[S.mat] : '') + ' | ' + okN + ' listed sizes within all reference ranges' + (top.length ? '; best: ' + top.slice(0, 3).map(function (x) { return x.row.id.toFixed(2) + ' × ' + x.row.cs.toFixed(2) + ' mm (squeeze ' + pct(x.e.squeeze) + '%, ' + FIT_TXT[S.app].s.toLowerCase() + ' ' + pct(x.e.fit) + '%, fill ' + pct(x.e.fill) + '%)'; }).join('; ') : '') + ' | ' + location.href;
        var review = btn(top.length ? 'ic-btn--dark' : 'ic-btn--primary', 'Request engineering review', ICON.wrench, rfqLink(ctx, top[0] && top[0].tier === 0 ? { id: top[0].row.id, cs: top[0].row.cs } : null));
        if (!top.length) {
          setVerdict('out', 'No listed size lands inside the reference ranges for this groove.', 'Check the dimensions, or send the groove for a custom-size quote and review.');
          drawGland(null); updateDock(null);
          var cu = btn('ic-btn--ghost', 'Request a custom size', ICON.arrow, rfq + '?source=groove-design&request_mode=CUSTOM&context=' + encodeURIComponent(ctx.slice(0, 1500)));
          setActions([review, cu]);
          return;
        }
        var best = top[0];
        setVerdict(okN ? 'ok' : 'check', okN ? okN + ' listed size' + (okN === 1 ? '' : 's') + ' fit this groove inside all three reference ranges.' : 'No size sits inside every range; the closest listed sizes are shown to check.', 'Ranked by closeness to the middle of the squeeze, ' + FIT_TXT[S.app].s.toLowerCase() + ' and fill ranges' + (S.mat ? ' · ' + MATS[S.mat] + ' only' : '') + '. ' + ix.rows.length.toLocaleString('en-US') + ' sizes searched.');
        keyBox.replaceChildren.apply(keyBox, corePlates(best.e, true));
        keyBox.insertBefore(el('p', 'ic-groove-key-cap', 'Best match: ' + best.row.id.toFixed(2) + ' × ' + best.row.cs.toFixed(2) + ' mm'), keyBox.firstChild);
        drawGland(geom(h, best.e, { cs: best.row.cs }));
        updateDock(best.e);
        // candidate table
        var wrap = el('div', 'ic-table-wrap ic-groove-cand-wrap'), table = el('table', 'ic-table ic-groove-cand');
        table.innerHTML = '<caption class="ic-sr">Listed O-ring sizes that fit this groove</caption><thead><tr><th scope="col">Size (ID × CS)</th><th scope="col">Squeeze</th><th scope="col">' + FIT_TXT[S.app].s + '</th><th scope="col">Fill</th><th scope="col">Listed grades</th><th scope="col"><span class="ic-sr">Action</span></th></tr></thead>';
        var tb = el('tbody'); table.appendChild(tb); wrap.appendChild(table);
        var gradesHost = el('div', 'ic-groove-grades-host');
        top.forEach(function (c, i) {
          var tr = el('tr', 'ic-groove-cand__row' + (c.tier ? ' is-check' : ''));
          var th = el('th', 'ic-groove-cand__size'); th.scope = 'row';
          th.appendChild(el('span', 'ic-num', c.row.id.toFixed(2) + ' × ' + c.row.cs.toFixed(2) + ' mm'));
          th.appendChild(el('small', 'ic-num', (c.row.id / IN).toFixed(3) + ' × ' + (c.row.cs / IN).toFixed(3) + ' in'));
          tr.appendChild(th);
          [['Squeeze', c.e.squeeze, c.e.st.squeeze], [FIT_TXT[S.app].s, c.e.fit, c.e.st.fit], ['Fill', c.e.fill, c.e.st.fill]].forEach(function (m) {
            var td = el('td', 'ic-groove-cand__m'); td.dataset.label = m[0];
            var v = el('span', 'ic-groove-dot ic-groove-dot--' + m[2] + ' ic-num', pct(m[1]) + '%'); v.title = ST_TXT[m[2]]; td.appendChild(v);
            td.appendChild(el('span', 'ic-sr', ' ' + ST_TXT[m[2]])); tr.appendChild(td);
          });
          var tg = el('td', 'ic-groove-cand__g'); tg.appendChild(gradeChips(c.row.codes)); tr.appendChild(tg);
          var ta = el('td', 'ic-groove-cand__a'); var b = btn(i === 0 ? 'ic-btn--dark ic-btn--sm' : 'ic-btn--ghost ic-btn--sm', 'Grades', null);
          b.setAttribute('aria-label', 'Show listed grades for ' + c.row.id.toFixed(2) + ' × ' + c.row.cs.toFixed(2) + ' mm');
          b.addEventListener('click', function () {
            $$('.ic-groove-cand__row.is-sel').forEach(function (r) { r.classList.remove('is-sel'); });
            tr.classList.add('is-sel');
            drawGland(geom(h, c.e, { cs: c.row.cs }));
            keyBox.replaceChildren.apply(keyBox, corePlates(c.e, true));
            keyBox.insertBefore(el('p', 'ic-groove-key-cap', 'Selected: ' + c.row.id.toFixed(2) + ' × ' + c.row.cs.toFixed(2) + ' mm'), keyBox.firstChild);
            updateDock(c.e);
            showGrades(gradesHost, c.row, null, true);
          });
          ta.appendChild(b); tr.appendChild(ta);
          tb.appendChild(tr);
        });
        var head = el('div', 'ic-groove-sub');
        head.appendChild(el('h3', 'ic-h4', 'Listed sizes for this groove'));
        head.appendChild(el('span', 'ic-note', 'Nearby sizes are not equivalents: compare the numbers before substituting.'));
        extra.replaceChildren(head, wrap, gradesHost);
        var bestRow = top[0].row;
        setActions([btn('ic-btn--primary', 'Show grades for ' + bestRow.id.toFixed(2) + ' × ' + bestRow.cs.toFixed(2), ICON.arrow), review]);
        actions.querySelector('.ic-btn--primary').addEventListener('click', function () { tb.querySelector('button').click(); });
        track('oxx_groove_result', { mode: 'find', application: S.app, motion: S.motion, unit_system: S.unit, result: okN ? 'ok' : 'check', matches: okN });
      }).catch(function () {
        if (run !== seq) return;
        setVerdict('out', 'The size index could not load.', 'Try again, or open the size finder.');
      });
    }

    /* ---------- mode: design ---------- */
    function renderDesign() {
      var rg = ring();
      if (!(rg.id > 0) || !(rg.cs > 0)) return empty('Enter an O-ring (dash number or ID × CS) to size a groove for it.');
      var hs = housing(), anchor = S.app === 'piston' ? hs.bore : S.app === 'rod' ? hs.rod : NaN;
      var o = design(rg, S.app, S.motion, anchor > 0 ? anchor : 0, data, S.unit), e = o.eval, h = o.h, u = S.unit;
      if (e.errors.length) { setVerdict('out', ERR[e.errors[0]] || 'This combination cannot be sized.', 'Check the fixed diameter.'); keyBox.replaceChildren(); moreBox.replaceChildren(); extra.replaceChildren(); drawGland(null); setActions([]); updateDock(null); return; }
      var warn = o.notes.filter(function (n) { return n !== 'rotary'; }).length || e.worst !== 'ok';
      setVerdict(warn ? 'check' : 'ok', 'Starting-point groove for ' + ringLabel(rg) + '.', APP_TXT[S.app] + ' · ' + MOTION_TXT[o.motion].toLowerCase() + (o.series ? ' · AS568 ' + o.series.toFixed(3) + ' in series widths and radii' : ' · width sized for about 68 % fill') + '.');
      var L = function (v) { return fmtL(v, u); };
      var dims = [];
      dims.push(plate({ k: 'Gland depth', v: L(o.depth), u: uLabel(u), big: true, alt: fmtAlt(o.depth, u), ref: 'Window ' + L(o.win[0]) + '–' + L(o.win[1]) + ' ' + uLabel(u) + ' for ' + e.ref.squeeze[0] + '–' + e.ref.squeeze[1] + ' % squeeze', note: isFace(S.app) ? 'Axial depth from the flange face.' : 'Radial depth from the mating surface to the groove floor.' }));
      dims.push(plate({ k: 'Groove width', v: L(o.width), u: uLabel(u), big: true, alt: fmtAlt(o.width, u) + (o.widthTol ? ' · tol ± ' + L(o.widthTol) : ''), ref: o.series ? (isFace(S.app) ? 'Sized for ≤ 75 % fill' : 'AS568 series width') : 'Sized for about 68 % fill', note: o.tight ? 'Tight reference width for gas or vacuum: ' + L(o.tight.w) + ' ' + uLabel(u) + ' (fill ' + pct(o.tight.fill) + ' %).' : '' }));
      var dia;
      if (S.app === 'piston') dia = plate({ k: 'Bore / groove Ø', v: L(h.bore) + ' / ' + L(h.groove), u: uLabel(u), big: true, alt: fmtAlt(h.bore, u) + ' / ' + fmtAlt(h.groove, u), note: anchor > 0 ? 'Bore as entered; groove diameter on the piston.' : 'Bore suggested from the ring; enter your bore to fix it.' });
      else if (S.app === 'rod') dia = plate({ k: 'Rod / groove Ø', v: L(h.rod) + ' / ' + L(h.groove), u: uLabel(u), big: true, alt: fmtAlt(h.rod, u) + ' / ' + fmtAlt(h.groove, u), note: anchor > 0 ? 'Rod as entered; groove diameter in the housing.' : 'Rod suggested from the ring; enter your rod to fix it.' });
      else dia = plate({ k: 'Groove ID / OD', v: L(h.gid) + ' / ' + L(h.god), u: uLabel(u), big: true, alt: fmtAlt(h.gid, u) + ' / ' + fmtAlt(h.god, u), note: S.app === 'face-int' ? 'Outer wall set about 1 % inside the ring for internal pressure.' : 'Inner wall set about 1.5 % over the ring ID for external pressure.' });
      dims.push(dia);
      keyBox.replaceChildren.apply(keyBox, dims);
      var more = corePlates(e, false);
      more.push(plate({ k: 'Corner radii', v: L(o.radii.r1) + ' / ' + L(o.radii.r2), u: uLabel(u), alt: 'R1 groove corner / R2 edge break', note: o.radii.exact ? 'AS568 series reference values.' : 'Interpolated from the AS568 series by cross-section.' }));
      var fin = o.finish.dynamic ? 'Ra ' + o.finish.dynamic[0] + '–' + o.finish.dynamic[1] + ' µin (' + (o.finish.dynamic[0] * 0.0254).toFixed(1) + '–' + (o.finish.dynamic[1] * 0.0254).toFixed(1) + ' µm) on dynamic surfaces' : 'Ra ≤ ' + o.finish.liquid + ' µin (' + (o.finish.liquid * 0.0254).toFixed(1) + ' µm) for liquids, ≤ ' + o.finish.gas + ' µin (' + (o.finish.gas * 0.0254).toFixed(1) + ' µm) for gas';
      more.push(plate({ k: 'Surface finish', v: o.finish.dynamic ? o.finish.dynamic[0] + '–' + o.finish.dynamic[1] : '≤ ' + o.finish.liquid, u: 'µin Ra', alt: fin, note: 'Sealing surfaces; reference values.' }));
      if (o.dovetail) more.push(plate({ k: 'Dovetail option', v: L(o.dovetail.w) + ' × ' + L(o.dovetail.d), u: uLabel(u), alt: 'Opening width × depth · R ' + L(o.dovetail.r1) + ' / ' + L(o.dovetail.r2), note: 'Retains the ring in the groove, e.g. on lids. Series reference.' }));
      moreBox.replaceChildren.apply(moreBox, more);
      drawGland(geom(h, e, rg));
      updateDock(e);
      // notes + public reference gland
      var ex = [];
      var notes = el('ul', 'ic-groove-notes');
      if (o.notes.indexOf('bore-conflict') >= 0) notes.appendChild(el('li', 'is-warn', 'No groove depth gives both the squeeze range and ≤ ' + data.rules.stretch[1] + ' % stretch with this bore. A ring with an ID near ' + fmtL(o.suggestId, u) + ' ' + uLabel(u) + ' suits this bore better.'));
      if (o.notes.indexOf('rod-fit') >= 0) notes.appendChild(el('li', 'is-warn', 'Rod interference is outside 0–2 %. A ring with an ID near ' + fmtL(o.suggestId, u) + ' ' + uLabel(u) + ' suits this rod better.'));
      if (o.notes.indexOf('rotary') >= 0) notes.appendChild(el('li', 'is-warn', 'Rotary seals depend on speed, heat build-up and ring ID sizing. Use this as a starting point and request a review.'));
      notes.appendChild(el('li', null, 'Nominal dimensions. Add machining tolerances so squeeze and fill stay inside their ranges at both limits.'));
      ex.push(notes);
      if (rg.dash) {
        var rh = refGland(data, rg.dash, rg, S.app, S.motion);
        var re = rh && evaluate(rh, rg, data);
        if (re && !re.errors.length && re.core !== 'out') {
          var box = el('div', 'ic-groove-ref');
          box.appendChild(el('h3', 'ic-h4', 'Public reference gland for AS568 -' + rg.dash));
          box.appendChild(el('p', 'ic-note', 'From a public AS568 gland table; a size reference, not a validation. Standard diameters are often easier to machine and gauge.'));
          var t = el('table', 'ic-table ic-table--spec ic-table--compact'), tb = el('tbody');
          var rows = S.app === 'piston' ? [['Bore', rh.bore], ['Groove Ø', rh.groove], ['Piston Ø', rh.clr], ['Width', rh.width]] : S.app === 'rod' ? [['Rod', rh.rod], ['Groove Ø', rh.groove], ['Housing bore at rod', rh.clr], ['Width', rh.width]] : [['Groove ID', rh.gid], ['Groove OD', rh.god], ['Depth', rh.depth]];
          rows.forEach(function (r) { if (!(r[1] > 0)) return; var tr = el('tr'); tr.appendChild(el('th', null, r[0])); var td = el('td', 'ic-num', fmtL(r[1], u) + ' ' + uLabel(u) + '  ·  ' + fmtAlt(r[1], u)); tr.appendChild(td); tb.appendChild(tr); });
          var tr2 = el('tr'); tr2.appendChild(el('th', null, 'Result')); tr2.appendChild(el('td', null, 'Squeeze ' + pct(re.squeeze) + ' % · ' + FIT_TXT[S.app].s.toLowerCase() + ' ' + pct(re.fit) + ' % · fill ' + pct(re.fill) + ' %')); tb.appendChild(tr2);
          t.appendChild(tb); var tw = el('div', 'ic-table-wrap'); tw.appendChild(t); box.appendChild(tw);
          var use = btn('ic-btn--ghost ic-btn--sm', 'Check this reference gland', ICON.gauge);
          use.addEventListener('click', function () { loadHousing(rh); selectMode('check'); scrollOut(); });
          box.appendChild(use);
          ex.push(box);
        }
      }
      extra.replaceChildren.apply(extra, ex);
      var ctx = baseCtx('Design a groove') + ' | O-ring ' + ringLabel(rg) + ' | Suggested: depth ' + L(o.depth) + ', width ' + L(o.width) + ', ' + housingText(h) + ', radii ' + L(o.radii.r1) + '/' + L(o.radii.r2) + ' ' + uLabel(u) + ' | ' + evalText(e) + ' | ' + location.href;
      var chk = btn('ic-btn--primary', 'Check this groove', ICON.gauge);
      chk.addEventListener('click', function () { loadHousing(h); selectMode('check'); scrollOut(); });
      var see = btn('ic-btn--ghost', 'See matching O-rings', ICON.search);
      see.addEventListener('click', function () { loadHousing(h); selectMode('find'); scrollOut(); });
      setActions([chk, see, btn('ic-btn--dark', 'Request engineering review', ICON.wrench, rfqLink(ctx, rg))]);
      track('oxx_groove_result', { mode: 'design', application: S.app, motion: S.motion, unit_system: S.unit, result: e.worst });
    }
    function loadHousing(h) {
      ['bore', 'groove', 'rod', 'width', 'clr', 'gid', 'god', 'depth'].forEach(function (k) {
        S.mm[k + ':' + S.app] = h[k] > 0 ? (S.unit === 'in' ? Math.round(h[k] / IN * 1000) / 1000 * IN : Math.round(h[k] * 100) / 100) : NaN;
      });
      setAll();
    }
    function scrollOut() { if (window.matchMedia('(max-width: 989px)').matches) out.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' }); }

    /* ---------- reference gland button (check mode) ---------- */
    function updateRefBtn() {
      if (!refBtn) return;
      var rg = ring(), rh = rg.dash && refGland(data, rg.dash, rg, S.app, S.motion), re = rh && evaluate(rh, rg, data);
      refBtn.hidden = !(re && !re.errors.length && re.core !== 'out');
      if (!refBtn.hidden) { refBtn.textContent = 'Fill the public reference groove for -' + rg.dash; refBtn.onclick = function () { loadHousing(rh); run(); }; }
    }

    /* ---------- run + URL ---------- */
    function run() {
      seq++;
      updateRefBtn();
      if (S.mode === 'check') renderCheck(); else if (S.mode === 'find') renderFind(); else renderDesign();
      clearTimeout(urlTimer); urlTimer = setTimeout(saveUrl, 400);
    }
    function saveUrl(now) {
      if (!window.history || !history.replaceState) return;
      var p = new URLSearchParams(location.search);
      ['mode', 'app', 'motion', 'units', 'dash', 'mat'].concat(KEYS).forEach(function (k) { p.delete(k); });
      p.set('mode', S.mode); p.set('app', S.app); p.set('motion', isFace(S.app) ? 'static' : S.motion); p.set('units', S.unit);
      KEYS.forEach(function (k) { if (S.mm[k] > 0) p.set(k, fmtL(S.mm[k], S.unit)); });
      ['bore', 'groove', 'rod', 'width', 'clr', 'gid', 'god', 'depth'].forEach(function (k) { if (S.mm[k + ':' + S.app] > 0) p.set(k, fmtL(S.mm[k + ':' + S.app], S.unit)); });
      if (S.dash) p.set('dash', S.dash);
      if (S.mat) p.set('mat', S.mat);
      history.replaceState(history.state, '', location.pathname + '?' + p.toString() + location.hash);
    }
    function readUrl() {
      var p = new URLSearchParams(location.search), has = false;
      if (/^(check|find|design)$/.test(p.get('mode'))) { S.mode = p.get('mode'); has = true; }
      if (APP_TXT[p.get('app')]) { S.app = p.get('app'); has = true; }
      if (MOTION_TXT[p.get('motion')]) S.motion = p.get('motion');
      if (/^(mm|in)$/.test(p.get('units'))) S.unit = p.get('units');
      if (MATS[p.get('mat')]) S.mat = p.get('mat');
      KEYS.forEach(function (k) { var v = num(p.get(k)); if (v > 0) S.mm[k] = v * (S.unit === 'in' ? IN : 1); });
      ['bore', 'groove', 'rod', 'width', 'clr', 'gid', 'god', 'depth'].forEach(function (k) { var v = num(p.get(k)); if (v > 0) { S.mm[k + ':' + S.app] = v * (S.unit === 'in' ? IN : 1); has = true; } });
      var d = String(p.get('dash') || '').replace(/\D/g, ''); if (d) S.dash = ('00' + d).slice(-3);
      return has;
    }

    /* ---------- events ---------- */
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { selectMode(t.dataset.icGrTab); });
      t.addEventListener('keydown', function (e) {
        var j = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null;
        if (j == null) return; e.preventDefault(); selectMode(tabs[(j + tabs.length) % tabs.length].dataset.icGrTab, true);
      });
    });
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'app') { S.app = t.value; rekey(); setAll(); applyVisibility(); run(); }
      else if (t.name === 'motion') { S.motion = t.value; run(); }
      else if (t.name === 'unit') {
        S.unit = t.value; setAll(); applyVisibility();
        if (S.dash && as568) resolveQuick(S.dash, false);
        run();
      } else if (t.getAttribute('data-ic-gr-mat') != null) { S.mat = t.value; run(); }
    });
    form.addEventListener('input', function (e) {
      var t = e.target;
      if (t.hasAttribute('data-ic-gr-f')) {
        readField(t);
        var key = t.getAttribute('data-ic-gr-f');
        if ((key === 'id' || key === 'cs') && S.dash) { S.dash = ''; quick.value = ''; quickMsg.textContent = ''; }
        clearTimeout(timer); timer = setTimeout(run, 180);
      } else if (t === quick) {
        clearTimeout(timer); timer = setTimeout(function () { resolveQuick(quick.value, true).then(function (ok) { if (ok) run(); }); }, 260);
      }
    });
    form.addEventListener('submit', function (e) { e.preventDefault(); clearTimeout(timer); run(); scrollOut(); });
    var clear = $('[data-ic-gr-clear]');
    if (clear) clear.addEventListener('click', function () {
      KEYS.concat(HOUSE_KEYS.map(function (k) { return k + ':' + S.app; })).forEach(function (k) { S.mm[k] = NaN; });
      setAll(); S.dash = ''; quick.value = ''; quickMsg.textContent = ''; run();
      var first = form.querySelector('.ic-groove-fs:not([hidden]) input.ic-input:not([hidden])'); if (first) first.focus();
    });
    var rz;
    window.addEventListener('resize', function () { clearTimeout(rz); rz = setTimeout(function () { if (lastGeom !== undefined) drawGland(lastGeom); }, 150); });

    /* ---------- boot ---------- */
    var fromUrl = readUrl();
    if (!fromUrl) { // worked example: AS568 -214 in a static piston groove
      S.mm['bore:piston'] = 31.75; S.mm['groove:piston'] = 26.11; S.mm['width:piston'] = 4.8; S.mm.id = 24.99; S.mm.cs = 3.53; S.dash = '214';
      root.classList.add('is-example');
    }
    $$('input[name="app"]').forEach(function (r) { r.checked = r.value === S.app; });
    $$('input[name="motion"]').forEach(function (r) { r.checked = r.value === S.motion; });
    $$('input[name="unit"]').forEach(function (r) { r.checked = r.value === S.unit; });
    var matSel = $('[data-ic-gr-mat]'); if (matSel) matSel.value = S.mat;
    rekey(); setAll();
    if (S.dash) { quick.value = '-' + S.dash; }
    tabs.forEach(function (t) { var on = t.dataset.icGrTab === S.mode; t.setAttribute('aria-selected', String(on)); t.tabIndex = on ? 0 : -1; if (on) panel.setAttribute('aria-labelledby', t.id); });
    applyVisibility();
    X.loadJSON(root.dataset.glandUrl).then(function (d) { if (d && d.rules) data = d; }).catch(function () {}).then(function () {
      return S.dash ? resolveQuick(S.dash, false) : null;
    }).then(function () { run(); });
    form.addEventListener('input', function once() { root.classList.remove('is-example'); form.removeEventListener('input', once); });
  }

  function boot() { document.querySelectorAll('[data-ic-groove]').forEach(init); }
  if (window.OXXIC) boot(); else document.addEventListener('DOMContentLoaded', boot);
  window.addEventListener('load', boot);
  document.addEventListener('shopify:section:load', boot);
})();
